import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DashboardLayout, Card, Button } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { projectStatusApi } from '../services/api';
import { clearTemplateCache } from '../services/projectStatusUtils';
import ProjectStatusTimeline from '../components/ProjectStatusTimeline';

interface SubStep {
  key: string;
  label_sv: string;
  label_en: string;
  dotColor: string;
  sortOrder: number;
}

interface StatusStep {
  sortOrder: number;
  key: string;
  label_sv: string;
  label_en: string;
  badgeColor: string;
  isSpecial: boolean;
  subSteps?: SubStep[];
  hideInTimeline?: boolean;
}

const BADGE_COLORS = ['secondary', 'info', 'primary', 'warning', 'success', 'danger'];
const REQUIRED_STATUS_KEYS = new Set([
  'not_started', 'contract', 'payment', 'in_progress', 'milestone',
  'on_hold', 'conflict', 'dispute', 'completed'
]);

export function AdminProjectConfig() {
  const { t } = useTranslation();
  const [baseSteps, setBaseSteps] = useState<StatusStep[]>([]);
  const [extraStepPattern, setExtraStepPattern] = useState<StatusStep[]>([]);
  const [previewMilestones, setPreviewMilestones] = useState(1);
  const [previewSteps, setPreviewSteps] = useState<StatusStep[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [editingStep, setEditingStep] = useState<{ section: 'base' | 'extra'; index: number } | null>(null);
  const [expandedSubSteps, setExpandedSubSteps] = useState<number | null>(null);

  useEffect(() => {
    loadTemplate();
  }, []);

  useEffect(() => {
    generatePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseSteps, extraStepPattern, previewMilestones]);

  const loadTemplate = async () => {
    try {
      const data = await projectStatusApi.getTemplate();
      setBaseSteps(data.baseSteps || []);
      setExtraStepPattern(data.extraStepPattern || []);
    } catch (error) {
      console.error('Failed to load template:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generatePreview = () => {
    const normalSteps = baseSteps.filter(s => !s.isSpecial);
    const specialSteps = baseSteps.filter(s => s.isSpecial);

    if (previewMilestones <= 1) {
      setPreviewSteps([...normalSteps, ...specialSteps]);
      return;
    }

    const result = [...normalSteps];
    let nextSortOrder = Math.max(...normalSteps.map(s => s.sortOrder), 0) + 1;

    for (let i = 1; i < previewMilestones; i++) {
      for (const pattern of extraStepPattern) {
        result.push({ ...pattern, sortOrder: nextSortOrder++ });
      }
    }

    result.push(...specialSteps);
    setPreviewSteps(result);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    try {
      const saved = await projectStatusApi.updateTemplate({ baseSteps, extraStepPattern });
      setBaseSteps(saved.baseSteps || []);
      setExtraStepPattern(saved.extraStepPattern || []);
      clearTemplateCache();
      setSaveMessage({ type: 'success', text: t('projectConfig.saved', 'Mallen har sparats') });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      setSaveMessage({ type: 'error', text: t('projectConfig.saveError', 'Kunde inte spara mallen') });
    } finally {
      setIsSaving(false);
    }
  };

  const addBaseStep = (isSpecial: boolean) => {
    const relevantSteps = baseSteps.filter(s => s.isSpecial === isSpecial);
    let newSortOrder: number;
    if (isSpecial) {
      newSortOrder = Math.max(...relevantSteps.map(s => s.sortOrder), 106) + 1;
    } else {
      const maxNormal = Math.max(...relevantSteps.map(s => s.sortOrder), 0);
      newSortOrder = maxNormal + 1;
    }

    const newStep: StatusStep = {
      sortOrder: newSortOrder,
      key: `step_${newSortOrder}`,
      label_sv: 'Nytt steg',
      label_en: 'New step',
      badgeColor: 'secondary',
      isSpecial
    };

    const updated = [...baseSteps, newStep].sort((a, b) => a.sortOrder - b.sortOrder);
    setBaseSteps(updated);
  };

  const removeBaseStep = (index: number) => {
    setBaseSteps(baseSteps.filter((_, i) => i !== index));
    setEditingStep(null);
  };

  const updateBaseStep = (index: number, field: keyof StatusStep, value: any) => {
    const updated = [...baseSteps];
    updated[index] = { ...updated[index], [field]: value };
    setBaseSteps(updated);
  };

  const moveBaseStep = (index: number, direction: 'up' | 'down') => {
    const isSpecial = baseSteps[index].isSpecial;
    // Find indices within same category
    const categoryIndices = baseSteps.map((s, i) => ({ s, i })).filter(({ s }) => s.isSpecial === isSpecial);
    const posInCategory = categoryIndices.findIndex(({ i }) => i === index);

    if (direction === 'up' && posInCategory > 0) {
      const swapIndex = categoryIndices[posInCategory - 1].i;
      const updated = [...baseSteps];
      const tempSort = updated[index].sortOrder;
      updated[index] = { ...updated[index], sortOrder: updated[swapIndex].sortOrder };
      updated[swapIndex] = { ...updated[swapIndex], sortOrder: tempSort };
      setBaseSteps(updated.sort((a, b) => a.sortOrder - b.sortOrder));
    } else if (direction === 'down' && posInCategory < categoryIndices.length - 1) {
      const swapIndex = categoryIndices[posInCategory + 1].i;
      const updated = [...baseSteps];
      const tempSort = updated[index].sortOrder;
      updated[index] = { ...updated[index], sortOrder: updated[swapIndex].sortOrder };
      updated[swapIndex] = { ...updated[swapIndex], sortOrder: tempSort };
      setBaseSteps(updated.sort((a, b) => a.sortOrder - b.sortOrder));
    }
  };

  const addExtraStep = () => {
    setExtraStepPattern([...extraStepPattern, {
      sortOrder: 0,
      key: `extra_${extraStepPattern.length}`,
      label_sv: 'Nytt steg',
      label_en: 'New step',
      badgeColor: 'secondary',
      isSpecial: false
    }]);
  };

  const removeExtraStep = (index: number) => {
    setExtraStepPattern(extraStepPattern.filter((_, i) => i !== index));
    setEditingStep(null);
  };

  const updateExtraStep = (index: number, field: keyof StatusStep, value: any) => {
    const updated = [...extraStepPattern];
    updated[index] = { ...updated[index], [field]: value };
    setExtraStepPattern(updated);
  };

  const moveExtraStep = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const updated = [...extraStepPattern];
      [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
      setExtraStepPattern(updated);
    } else if (direction === 'down' && index < extraStepPattern.length - 1) {
      const updated = [...extraStepPattern];
      [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
      setExtraStepPattern(updated);
    }
  };

  const addSubStep = (stepIndex: number) => {
    const updated = [...baseSteps];
    if (!updated[stepIndex].subSteps) updated[stepIndex].subSteps = [];
    updated[stepIndex].subSteps!.push({
      key: `sub_${Date.now()}`, label_sv: 'Nytt substeg', label_en: 'New substep',
      dotColor: 'grey', sortOrder: updated[stepIndex].subSteps!.length + 1
    });
    setBaseSteps(updated);
  };

  const removeSubStep = (stepIndex: number, subIndex: number) => {
    const updated = [...baseSteps];
    updated[stepIndex].subSteps = updated[stepIndex].subSteps!.filter((_, i) => i !== subIndex);
    setBaseSteps(updated);
  };

  const updateSubStep = (stepIndex: number, subIndex: number, field: keyof SubStep, value: any) => {
    const updated = [...baseSteps];
    updated[stepIndex].subSteps![subIndex] = { ...updated[stepIndex].subSteps![subIndex], [field]: value };
    setBaseSteps(updated);
  };

  const renderStepRow = (
    step: StatusStep,
    index: number,
    section: 'base' | 'extra',
    onUpdate: (i: number, field: keyof StatusStep, value: any) => void,
    onRemove: (i: number) => void,
    onMove: (i: number, dir: 'up' | 'down') => void,
    totalInCategory: number,
    posInCategory: number
  ) => {
    const isEditing = editingStep?.section === section && editingStep?.index === index;

    return (
      <tr key={`${section}-${index}`}>
        <td className="text-center" style={{ width: 60 }}>
          <span className="badge bg-light text-dark">{step.sortOrder || '-'}</span>
        </td>
        <td>
          {isEditing ? (
            <input
              type="text"
              className="form-control form-control-sm"
              value={step.label_sv}
              onChange={(e) => onUpdate(index, 'label_sv', e.target.value)}
            />
          ) : (
            <span>{step.label_sv}</span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="text"
              className="form-control form-control-sm"
              value={step.label_en}
              onChange={(e) => onUpdate(index, 'label_en', e.target.value)}
            />
          ) : (
            <span className="text-muted">{step.label_en}</span>
          )}
        </td>
        <td>
          {isEditing ? (
            <input
              type="text"
              className="form-control form-control-sm"
              value={step.key}
              disabled={REQUIRED_STATUS_KEYS.has(step.key)}
              onChange={(e) => onUpdate(index, 'key', e.target.value)}
            />
          ) : (
            <code className="small">{step.key}</code>
          )}
        </td>
        <td className="text-center">
          {isEditing ? (
            <select
              className="form-select form-select-sm"
              value={step.badgeColor}
              onChange={(e) => onUpdate(index, 'badgeColor', e.target.value)}
            >
              {BADGE_COLORS.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          ) : (
            <span className={`badge bg-${step.badgeColor}`}>{step.badgeColor}</span>
          )}
          {isEditing && (
            <label className="form-check-label small ms-2">
              <input type="checkbox" className="form-check-input" checked={step.hideInTimeline || false}
                onChange={e => onUpdate(index, 'hideInTimeline', e.target.checked)} /> {t('adminProjectConfig.fields.hideLabel')}
            </label>
          )}
        </td>
        <td className="text-center" style={{ width: 120 }}>
          <div className="btn-group btn-group-sm">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={posInCategory === 0}
              onClick={() => onMove(index, 'up')}
              title={t('adminProjectConfig.actions.moveUp')}
            >
              <i className="bi bi-arrow-up" style={{ fontSize: '0.7rem' }}>{'\u2191'}</i>
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={posInCategory === totalInCategory - 1}
              onClick={() => onMove(index, 'down')}
              title={t('adminProjectConfig.actions.moveDown')}
            >
              <i className="bi bi-arrow-down" style={{ fontSize: '0.7rem' }}>{'\u2193'}</i>
            </button>
            <button
              className={`btn btn-sm ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setEditingStep(isEditing ? null : { section, index })}
              title={isEditing ? t('adminProjectConfig.actions.done') : t('adminProjectConfig.actions.edit')}
            >
              {isEditing ? '\u2713' : '\u270E'}
            </button>
            <button
              className={`btn btn-sm ${expandedSubSteps === index ? 'btn-info text-white' : 'btn-outline-info'}`}
              onClick={() => setExpandedSubSteps(expandedSubSteps === index ? null : index)}
              title={t('adminProjectConfig.actions.toggleSubSteps')}
            >
              {t('adminProjectConfig.substeps.toggleButton', { count: step.subSteps?.length || 0 })}
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              disabled={REQUIRED_STATUS_KEYS.has(step.key)}
              onClick={() => onRemove(index)}
              title={REQUIRED_STATUS_KEYS.has(step.key) ? t('adminProjectConfig.actions.systemKeyInUse') : t('adminProjectConfig.actions.remove')}
            >
              \u2715
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const normalSteps = baseSteps.filter(s => !s.isSpecial);
  const specialSteps = baseSteps.filter(s => s.isSpecial);

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Breadcrumb items={[
        { label: t('admin.nav.system', 'System'), path: '/admin/system-menu' },
        { label: t('projectConfig.title', 'Projekt konfiguration') }
      ]} />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">{t('projectConfig.title', 'Projekt konfiguration')}</h4>
        <div className="d-flex align-items-center gap-2">
          {saveMessage && (
            <span className={`text-${saveMessage.type === 'success' ? 'success' : 'danger'} small`}>
              {saveMessage.text}
            </span>
          )}
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? t('common.saving', 'Sparar...') : t('common.save', 'Spara')}
          </Button>
        </div>
      </div>

      <div className="alert alert-info">
        {t(
          'projectConfig.keyProtection',
          'Systemnycklarna identifierar befintliga projekts status och kan därför inte bytas eller tas bort. Namn, färger, substeg och ordning kan ändras.'
        )}
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        {/* Section 1: Default Status Template - Normal Steps */}
        <Card className="mb-4">
        <h5 className="mb-1">{t('projectConfig.defaultTemplate', 'Default projekt status mall')}</h5>
        <p className="text-muted small mb-3">
          {t('projectConfig.defaultTemplateDesc', 'Dessa steg tilldelas alla nya projekt. Linjära steg som visar projektets progression.')}
        </p>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th className="text-center" style={{ width: 60 }}>#</th>
                <th>{t('projectConfig.labelSv', 'Namn (SV)')}</th>
                <th>{t('projectConfig.labelEn', 'Namn (EN)')}</th>
                <th>{t('projectConfig.key', 'Nyckel')}</th>
                <th className="text-center">{t('projectConfig.badge', 'Färg')}</th>
                <th className="text-center" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {normalSteps.map((step, i) => {
                const globalIndex = baseSteps.indexOf(step);
                return (
                  <React.Fragment key={`normal-${globalIndex}`}>
                    {renderStepRow(step, globalIndex, 'base', updateBaseStep, removeBaseStep, moveBaseStep, normalSteps.length, i)}
                    {expandedSubSteps === globalIndex && (
                      <tr><td colSpan={6}>
                        <div className="bg-light p-3 rounded border">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold">{t('adminProjectConfig.substeps.heading', { label: step.label_sv })}</span>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => addSubStep(globalIndex)}>{t('adminProjectConfig.substeps.add')}</button>
                          </div>
                          {(!step.subSteps || step.subSteps.length === 0) ? (
                            <p className="text-muted small mb-0">{t('adminProjectConfig.substeps.empty')}</p>
                          ) : (
                            <table className="table table-sm mb-0 bg-white">
                              <thead><tr>
                                <th style={{ width: 30 }}>#</th>
                                <th>{t('adminProjectConfig.substeps.table.nameSv')}</th>
                                <th>{t('adminProjectConfig.substeps.table.nameEn')}</th>
                                <th>{t('adminProjectConfig.substeps.table.key')}</th>
                                <th style={{ width: 100 }}>{t('adminProjectConfig.substeps.table.dotColor')}</th>
                                <th style={{ width: 50 }}></th>
                              </tr></thead>
                              <tbody>
                                {step.subSteps.map((sub, si) => (
                                  <tr key={si}>
                                    <td className="text-muted small">{si + 1}</td>
                                    <td><input className="form-control form-control-sm" value={sub.label_sv} onChange={e => updateSubStep(globalIndex, si, 'label_sv', e.target.value)} /></td>
                                    <td><input className="form-control form-control-sm" value={sub.label_en} onChange={e => updateSubStep(globalIndex, si, 'label_en', e.target.value)} /></td>
                                    <td><input className="form-control form-control-sm" value={sub.key} onChange={e => updateSubStep(globalIndex, si, 'key', e.target.value)} /></td>
                                    <td>
                                      <div className="d-flex align-items-center gap-1">
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: sub.dotColor === 'green' ? '#28a745' : sub.dotColor === 'blue' ? '#007bff' : sub.dotColor === 'red' ? '#dc3545' : '#dee2e6', flexShrink: 0 }} />
                                        <select className="form-select form-select-sm" value={sub.dotColor} onChange={e => updateSubStep(globalIndex, si, 'dotColor', e.target.value)}>
                                          <option value="green">{t('adminProjectConfig.substeps.dotColors.green')}</option>
                                          <option value="blue">{t('adminProjectConfig.substeps.dotColors.blue')}</option>
                                          <option value="grey">{t('adminProjectConfig.substeps.dotColors.grey')}</option>
                                          <option value="red">{t('adminProjectConfig.substeps.dotColors.red')}</option>
                                        </select>
                                      </div>
                                    </td>
                                    <td><button className="btn btn-sm btn-outline-danger" onClick={() => removeSubStep(globalIndex, si)}>{'\u2715'}</button></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => addBaseStep(false)}>
            + {t('projectConfig.addStep', 'Lägg till steg')}
          </button>
        </div>
        </Card>

        {/* Section 2: Special/Terminal Statuses */}
        <Card className="mb-4">
        <h5 className="mb-1">{t('projectConfig.specialStatuses', 'Speciella statusar')}</h5>
        <p className="text-muted small mb-3">
          {t('projectConfig.specialStatusesDesc', 'Statusar som kan nås från flera steg (vilande, tvist, avslutat). Visas separat från den linjära progressionen.')}
        </p>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th className="text-center" style={{ width: 60 }}>#</th>
                <th>{t('projectConfig.labelSv', 'Namn (SV)')}</th>
                <th>{t('projectConfig.labelEn', 'Namn (EN)')}</th>
                <th>{t('projectConfig.key', 'Nyckel')}</th>
                <th className="text-center">{t('projectConfig.badge', 'Färg')}</th>
                <th className="text-center" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {specialSteps.map((step, i) => {
                const globalIndex = baseSteps.indexOf(step);
                return (
                  <React.Fragment key={`special-${globalIndex}`}>
                    {renderStepRow(step, globalIndex, 'base', updateBaseStep, removeBaseStep, moveBaseStep, specialSteps.length, i)}
                    {expandedSubSteps === globalIndex && (
                      <tr><td colSpan={6}>
                        <div className="bg-light p-3 rounded border">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="fw-semibold">{t('adminProjectConfig.substeps.heading', { label: step.label_sv })}</span>
                            <button className="btn btn-sm btn-outline-primary" onClick={() => addSubStep(globalIndex)}>{t('adminProjectConfig.substeps.add')}</button>
                          </div>
                          {(!step.subSteps || step.subSteps.length === 0) ? (
                            <p className="text-muted small mb-0">{t('adminProjectConfig.substeps.empty')}</p>
                          ) : (
                            <table className="table table-sm mb-0 bg-white">
                              <thead><tr>
                                <th style={{ width: 30 }}>#</th>
                                <th>{t('adminProjectConfig.substeps.table.nameSv')}</th>
                                <th>{t('adminProjectConfig.substeps.table.nameEn')}</th>
                                <th>{t('adminProjectConfig.substeps.table.key')}</th>
                                <th style={{ width: 100 }}>{t('adminProjectConfig.substeps.table.dotColor')}</th>
                                <th style={{ width: 50 }}></th>
                              </tr></thead>
                              <tbody>
                                {step.subSteps.map((sub, si) => (
                                  <tr key={si}>
                                    <td className="text-muted small">{si + 1}</td>
                                    <td><input className="form-control form-control-sm" value={sub.label_sv} onChange={e => updateSubStep(globalIndex, si, 'label_sv', e.target.value)} /></td>
                                    <td><input className="form-control form-control-sm" value={sub.label_en} onChange={e => updateSubStep(globalIndex, si, 'label_en', e.target.value)} /></td>
                                    <td><input className="form-control form-control-sm" value={sub.key} onChange={e => updateSubStep(globalIndex, si, 'key', e.target.value)} /></td>
                                    <td>
                                      <div className="d-flex align-items-center gap-1">
                                        <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: sub.dotColor === 'green' ? '#28a745' : sub.dotColor === 'blue' ? '#007bff' : sub.dotColor === 'red' ? '#dc3545' : '#dee2e6', flexShrink: 0 }} />
                                        <select className="form-select form-select-sm" value={sub.dotColor} onChange={e => updateSubStep(globalIndex, si, 'dotColor', e.target.value)}>
                                          <option value="green">{t('adminProjectConfig.substeps.dotColors.green')}</option>
                                          <option value="blue">{t('adminProjectConfig.substeps.dotColors.blue')}</option>
                                          <option value="grey">{t('adminProjectConfig.substeps.dotColors.grey')}</option>
                                          <option value="red">{t('adminProjectConfig.substeps.dotColors.red')}</option>
                                        </select>
                                      </div>
                                    </td>
                                    <td><button className="btn btn-sm btn-outline-danger" onClick={() => removeSubStep(globalIndex, si)}>{'\u2715'}</button></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-primary" onClick={() => addBaseStep(true)}>
            + {t('projectConfig.addSpecialStatus', 'Lägg till specialstatus')}
          </button>
        </div>
        </Card>

        {/* Section 3: Extra Step Pattern */}
        <Card className="mb-4">
        <h5 className="mb-1">{t('projectConfig.extraSteps', 'Default projekt status mall - extra steg')}</h5>
        <p className="text-muted small mb-3">
          {t('projectConfig.extraStepsDesc', 'Dessa steg läggs till efter sista linjära steget för varje extra etappmål utöver det första. T.ex. om ett projekt har 3 etappmål, läggs detta mönster till 2 gånger.')}
        </p>
        <div className="table-responsive">
          <table className="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th className="text-center" style={{ width: 60 }}>#</th>
                <th>{t('projectConfig.labelSv', 'Namn (SV)')}</th>
                <th>{t('projectConfig.labelEn', 'Namn (EN)')}</th>
                <th>{t('projectConfig.key', 'Nyckel')}</th>
                <th className="text-center">{t('projectConfig.badge', 'Färg')}</th>
                <th className="text-center" style={{ width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {extraStepPattern.map((step, i) =>
                renderStepRow(step, i, 'extra', updateExtraStep, removeExtraStep, moveExtraStep, extraStepPattern.length, i)
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-2">
          <button className="btn btn-sm btn-outline-primary" onClick={addExtraStep}>
            + {t('projectConfig.addExtraStep', 'Lägg till extra steg')}
          </button>
        </div>
        </Card>
      </fieldset>

      {/* Section 4: Preview */}
      <Card className="mb-4">
        <h5 className="mb-1">{t('projectConfig.preview', 'Förhandsgranskning')}</h5>
        <p className="text-muted small mb-3">
          {t('projectConfig.previewDesc', 'Så här ser statusmallen ut beroende på antal etappmål i projektet.')}
        </p>
        <div className="d-flex align-items-center gap-3 mb-3">
          <label className="form-label mb-0 small fw-semibold">
            {t('projectConfig.milestoneCount', 'Antal etappmål')}:
          </label>
          <input
            type="range"
            className="form-range"
            style={{ width: 200 }}
            min={1}
            max={10}
            value={previewMilestones}
            onChange={(e) => setPreviewMilestones(parseInt(e.target.value))}
          />
          <span className="badge bg-primary">{previewMilestones}</span>
        </div>

        {/* Visual stepper preview */}
        <ProjectStatusTimeline
          status={previewSteps.filter(s => !s.isSpecial && !s.hideInTimeline)[0]?.key || 'contract'}
          subStatus="draft"
          steps={previewSteps}
        />

        {/* Summary table */}
        <div className="mt-3">
          <small className="text-muted">
            {t('projectConfig.totalSteps', 'Totalt antal steg')}: <strong>{previewSteps.length}</strong>
            {' | '}
            {t('projectConfig.linearSteps', 'Linjära')}: <strong>{previewSteps.filter(s => !s.isSpecial).length}</strong>
            {' | '}
            {t('projectConfig.specialCount', 'Speciella')}: <strong>{previewSteps.filter(s => s.isSpecial).length}</strong>
          </small>
        </div>
      </Card>
    </DashboardLayout>
  );
}
